import {MaterialNode} from "#material/nodes/base/material/material-node"
import {registerMaterialGraphNode} from "#material/register"
import type {GeometrySockets} from "#material/nodes/converter/geometry/definition"
import {geometryDefinition} from "#material/nodes/converter/geometry/definition"
import type {ThreeReturns} from "#material/sockets/three"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import type {GeometryNode} from "#material/cycles/types"
import {threeValue, threeVec3} from "#material/three/three-utils"
import {ThreeTsl as TSL} from "#material/three/exports"

@registerMaterialGraphNode
export class Geometry extends MaterialNode<GeometrySockets> {
    public override get definition() {
        return geometryDefinition
    }

    override toThree = async (_evaluator: ThreeEvaluator): Promise<ThreeReturns<GeometrySockets>> => {
        // the winding alone does not say which way the cross product points, so the sign keeps it with the shading normal
        const faceNormal = TSL.dFdx(TSL.positionWorld).cross(TSL.dFdy(TSL.positionWorld)).normalize()

        return {
            position: TSL.positionWorld,
            normal: TSL.normalWorld,
            trueNormal: faceNormal.mul(TSL.sign(faceNormal.dot(TSL.normalWorld))),
            tangent: TSL.tangentWorld,
            incoming: TSL.cameraPosition.sub(TSL.positionWorld).normalize(),
            backfacing: TSL.float(TSL.frontFacing).oneMinus(),
            parametric: threeVec3({x: 0, y: 0, z: 0}),
            pointiness: threeValue(0),
            randomPerIsland: threeValue(0),
        }
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<GeometrySockets>> => {
        const geometryNode: GeometryNode = {
            type: "geometry",
            inputs: {},
            parameters: {},
            resources: {},
        }

        return {
            backfacing: [geometryNode, "backfacing"],
            position: [geometryNode, "position"],
            normal: [geometryNode, "normal"],
            tangent: [geometryNode, "tangent"],
            incoming: [geometryNode, "incoming"],
            parametric: [geometryNode, "parametric"],
            pointiness: [geometryNode, "pointiness"],
            randomPerIsland: [geometryNode, "random_per_island"],
            trueNormal: [geometryNode, "true_normal"],
        }
    }
}
